// RouteLink.js
import React, { Suspense } from "react";
import { Navigate } from "react-router-dom";

// Lazy load components for code splitting
const LoginPage = React.lazy(() => import("./Pages/Login/LoginPage"));
const DashboardLayout = React.lazy(() => import("./Layout/DashboardLayout"));
const DashboardHome = React.lazy(
  () => import("./Pages/Dashboard/DashboardHome"),
);
const PrivateRoute = React.lazy(() => import("./Utilities/PrivateRoute"));
const PermissionRoute = React.lazy(() => import("./Utilities/PermissionRoute"));
const AddUser = React.lazy(() => import("./Pages/Users/AddUser"));
const UserList = React.lazy(() => import("./Pages/Users/UserList"));
const UserPermissionList = React.lazy(
  () => import("./Pages/Users/UserPermissionList"),
);

// Zone Category Components
const ZoneCategoryList = React.lazy(
  () => import("./Pages/ZoneCategory/ZoneCategoryList"),
);
const AddZoneCategory = React.lazy(
  () => import("./Pages/ZoneCategory/AddZoneCategory"),
);

// Zone Components
const ZoneList = React.lazy(() => import("./Pages/Zone/ZoneList"));
const AddZone = React.lazy(() => import("./Pages/Zone/AddZone"));

// Package Components
const PackageList = React.lazy(() => import("./Pages/Package/PackageList"));
const AddPackage = React.lazy(() => import("./Pages/Package/AddPackage"));

// Feature Components
const FeatureList = React.lazy(() => import("./Pages/Feature/FeatureList"));
const AddFeature = React.lazy(() => import("./Pages/Feature/AddFeature"));

// PackageFeature Components
const PackageFeatureList = React.lazy(
  () => import("./Pages/PackageFeature/PackageFeatureList"),
);
const AddPackageFeature = React.lazy(
  () => import("./Pages/PackageFeature/AddPackageFeature"),
);

// Nationality Components
const NationalityList = React.lazy(
  () => import("./Pages/Nationality/NationalityList"),
);
const AddNationality = React.lazy(
  () => import("./Pages/Nationality/AddNationality"),
);

// PageSeo Components
const PageSeoList = React.lazy(
  () => import("./Pages/PageSeo/PageSeoList"),
);
const AddPageSeo = React.lazy(
  () => import("./Pages/PageSeo/AddPageSeo"),
);

// Blog Components
const BlogList = React.lazy(() => import("./Pages/Blog/BlogList"));
const AddBlog = React.lazy(() => import("./Pages/Blog/AddBlog"));

// News Components
const NewsList = React.lazy(() => import("./Pages/News/NewsList"));
const AddNews = React.lazy(() => import("./Pages/News/AddNews"));

// Gallery Components
const GalleryList = React.lazy(() => import("./Pages/Gallery/GalleryList"));
const AddGallery = React.lazy(() => import("./Pages/Gallery/AddGallery"));

// Customer Components
const CustomerList = React.lazy(
  () => import("./Pages/Customer/CustomerList"),
);
const AddCustomer = React.lazy(
  () => import("./Pages/Customer/AddCustomer"),
);

// Activity Components
const ActivityList = React.lazy(
  () => import("./Pages/Activity/ActivityList"),
);
const AddActivity = React.lazy(
  () => import("./Pages/Activity/AddActivity"),
);

// B2B Components
const B2BList = React.lazy(() => import("./Pages/B2B/B2BList"));
const AddB2B = React.lazy(() => import("./Pages/B2B/AddB2B"));

// Proposal Components
const ProposalList = React.lazy(
  () => import("./Pages/Proposal/ProposalList"),
);
const ProposalAdd = React.lazy(
  () => import("./Pages/Proposal/ProposalAdd"),
);
const ProposalPdf = React.lazy(
  () => import("./Pages/Proposal/ProposalPdf"),
);

// Website Proposal Calculator (Leads menu)
const WebProposalList = React.lazy(
  () => import("./Pages/WebProposal/WebProposalList"),
);
const WebProposalDetail = React.lazy(
  () => import("./Pages/WebProposal/WebProposalDetail"),
);
const WebsiteZones = React.lazy(
  () => import("./Pages/WebProposal/WebsiteZones"),
);
const WebProposalEmailSettings = React.lazy(
  () => import("./Pages/WebProposal/WebProposalEmailSettings"),
);
const WebProposalBranding = React.lazy(
  () => import("./Pages/WebProposal/WebProposalBranding"),
);
const WebProposalConfig = React.lazy(
  () => import("./Pages/WebProposal/WebProposalConfig"),
);

const routes = [
  {
    path: "/login",
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: "/",
    element: <Navigate to="/login" replace />,
  },
  {
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <PrivateRoute />
      </Suspense>
    ), // 🛡 Wrap protected routes here
    children: [
      {
        path: "/proposalpdf/:code",
        element: (
          <Suspense fallback={<div>Loading...</div>}>
            <ProposalPdf />
          </Suspense>
        ),
      },
      {
        path: "/dashboard",
        element: (
          <Suspense fallback={<div>Loading...</div>}>
            <DashboardLayout />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <DashboardHome />
              </Suspense>
            ),
          },
          {
            path: "useradd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/userlist"
                  requireAny={["canCreate", "canEdit"]}
                >
                  <AddUser />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "userlist",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/userlist"
                  requiredAction="canView"
                >
                  <UserList />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "userpermission",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/userpermission"
                  requiredAction="canView"
                >
                  <UserPermissionList />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "nationality",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/nationality"
                  requiredAction="canView"
                >
                  <NationalityList />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "nationalityadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/nationality"
                  requireAny={["canCreate", "canEdit"]}
                >
                  <AddNationality />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "zonecategory",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/zonecategory"
                  requiredAction="canView"
                >
                  <ZoneCategoryList />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "zonecategoryadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/zonecategory"
                  requireAny={["canCreate", "canEdit"]}
                >
                  <AddZoneCategory />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "zone",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/zone"
                  requiredAction="canView"
                >
                  <ZoneList />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "zoneadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/zone"
                  requireAny={["canCreate", "canEdit"]}
                >
                  <AddZone />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "package",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/package"
                  requiredAction="canView"
                >
                  <PackageList />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "packageadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/package"
                  requireAny={["canCreate", "canEdit"]}
                >
                  <AddPackage />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "feature",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/feature"
                  requiredAction="canView"
                >
                  <FeatureList />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "featureadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/feature"
                  requireAny={["canCreate", "canEdit"]}
                >
                  <AddFeature />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "packagefeature",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/packagefeature"
                  requiredAction="canView"
                >
                  <PackageFeatureList />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "packagefeatureadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/packagefeature"
                  requireAny={["canCreate", "canEdit"]}
                >
                  <AddPackageFeature />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "pageseo",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/pageseo"
                  requiredAction="canView"
                >
                  <PageSeoList />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "pageseoadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/pageseo"
                  requireAny={["canCreate", "canEdit"]}
                >
                  <AddPageSeo />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "blog",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/blog"
                  requiredAction="canView"
                >
                  <BlogList />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "blogadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/blog"
                  requireAny={["canCreate", "canEdit"]}
                >
                  <AddBlog />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "news",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/news"
                  requiredAction="canView"
                >
                  <NewsList />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "newsadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/news"
                  requireAny={["canCreate", "canEdit"]}
                >
                  <AddNews />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "gallery",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/gallery"
                  requiredAction="canView"
                >
                  <GalleryList />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "galleryadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/gallery"
                  requireAny={["canCreate", "canEdit"]}
                >
                  <AddGallery />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "customer",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/customer"
                  requiredAction="canView"
                >
                  <CustomerList />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "customeradd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/customer"
                  requireAny={["canCreate", "canEdit"]}
                >
                  <AddCustomer />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "Activity",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/Activity"
                  requiredAction="canView"
                >
                  <ActivityList />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "b2b",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/b2b"
                  requiredAction="canView"
                >
                  <B2BList />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "b2badd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/b2b"
                  requireAny={["canCreate", "canEdit"]}
                >
                  <AddB2B />
                </PermissionRoute>
              </Suspense>
            ),
          },
          {
            path: "proposal",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <ProposalList />
              </Suspense>
            ),
          },
          {
            path: "Proposal",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <ProposalList />
              </Suspense>
            ),
          },
          {
            path: "webproposal",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <WebProposalList />
              </Suspense>
            ),
          },
          {
            path: "webproposal/:id",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <WebProposalDetail />
              </Suspense>
            ),
          },
          {
            path: "websitezones",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <WebsiteZones />
              </Suspense>
            ),
          },
          {
            path: "webproposal-email",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <WebProposalEmailSettings />
              </Suspense>
            ),
          },
          {
            path: "webproposal-branding",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <WebProposalBranding />
              </Suspense>
            ),
          },
          {
            path: "webproposal-config",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <WebProposalConfig />
              </Suspense>
            ),
          },
          {
            path: "proposaladd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <ProposalAdd />
              </Suspense>
            ),
          },
          {
            path: "Activityadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PermissionRoute
                  path="/dashboard/Activity"
                  requireAny={["canCreate", "canEdit"]}
                >
                  <AddActivity />
                </PermissionRoute>
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
];

export default routes;
